import { useState, useMemo, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { HStack, VStack, Button, IconButton, Select, Box, Text, Badge, Wrap, WrapItem, Checkbox, useDisclosure } from "@chakra-ui/react";
import { PageHeader, SectionCard, DataTable, StatusBadge, CurrencyText, EmptyState } from "../../../shared/ui";
import { Plus, ArrowRight, Wallet, Pencil, Copy } from "../../../shared/icons";
import { STATUS_META, STATUS_ORDEM, SCHEDULE_STATUS_META } from "../../plantoes/utils/statusPlantao";
import { usePlantoesQuery } from "../../plantoes/hooks/usePlantoesQuery";
import { useVinculosQuery } from "../../vinculos/hooks/useVinculosQuery";
import EditShiftModal from "../../plantoes/components/EditShiftModal";
import RegistrarPlantaoModal from "../../plantoes/components/RegistrarPlantaoModal";
import BulkStatusBar from "../../plantoes/components/BulkStatusBar";
import PeriodFilterPopover from "../components/PeriodFilterPopover";
import { describePaymentMonth } from "../../plantoes/utils/paymentMonth";

// "Cobrar" abre a comprovação já no mês do plantão atrasado (row.data = "dd/mm/aaaa").
function urlCobranca(row) {
  const [, mes, ano] = (row.data ?? "").split("/");
  const periodo = ano && mes ? `&ano=${ano}&mes=${Number(mes)}` : "";
  return `/relatorios?vinculo=${row.vinculo_id}${periodo}`;
}

export default function RecebiveisPage() {
  const navigate = useNavigate();
  const { registrarPlantaoDisclosure } = useOutletContext() ?? {};
  const [statusFiltro, setStatusFiltro] = useState("");
  const [vinculoFiltro, setVinculoFiltro] = useState("");
  const [periodoFiltro, setPeriodoFiltro] = useState(null); // null = todos os períodos
  const [plantaoEditando, setPlantaoEditando] = useState(null);
  const edicaoDisclosure = useDisclosure();
  const [plantaoDuplicando, setPlantaoDuplicando] = useState(null);
  const duplicarDisclosure = useDisclosure();

  const { data: vinculos = [] } = useVinculosQuery();
  // Busca sem filtro de status para manter os contadores dos chips corretos; o filtro de
  // status é aplicado em memória (mesma lógica do backend, que também filtra status em
  // memória por ser um valor calculado). Vínculo e período são filtrados a nível de banco.
  const filtros = useMemo(() => {
    const f = {};
    if (vinculoFiltro) f.vinculoId = vinculoFiltro;
    if (periodoFiltro?.inicio && periodoFiltro?.fim) {
      f.dataInicio = periodoFiltro.inicio;
      f.dataFim = periodoFiltro.fim;
    } else if (periodoFiltro) {
      // O mês escolhido é o mês em que o pagamento cai (issue #18): outubro lista os
      // plantões de setembro. O período personalizado continua pela data trabalhada.
      f.anoPagamento = periodoFiltro.ano;
      f.mesPagamento = periodoFiltro.mes;
    }
    return f;
  }, [vinculoFiltro, periodoFiltro]);
  const { data, isLoading } = usePlantoesQuery(filtros);

  const todos = useMemo(() => data?.plantoes ?? [], [data]);
  const filtrados = useMemo(
    () => (statusFiltro ? todos.filter((p) => p.status === statusFiltro) : todos),
    [todos, statusFiltro]
  );
  const total = filtrados.reduce((acc, p) => acc + p.valor, 0);

  // Seleção para mudar o status de vários plantões de uma vez (ex.: filtrar um vínculo e
  // marcar todos como NF emitida). Trocar de filtro limpa a seleção para nunca agir sobre
  // plantões que saíram da tela.
  const [selecionados, setSelecionados] = useState(() => new Set());
  useEffect(() => setSelecionados(new Set()), [statusFiltro, vinculoFiltro, periodoFiltro]);
  const plantoesSelecionados = filtrados.filter((p) => selecionados.has(p.id));
  const todosSelecionados = filtrados.length > 0 && plantoesSelecionados.length === filtrados.length;
  const algunsSelecionados = plantoesSelecionados.length > 0 && !todosSelecionados;
  const alternarSelecao = (id) =>
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  const alternarTodos = () => setSelecionados(todosSelecionados ? new Set() : new Set(filtrados.map((p) => p.id)));
  const limparSelecao = () => setSelecionados(new Set());

  const renderSelecao = (row) => (
    <Checkbox
      isChecked={selecionados.has(row.id)}
      onChange={() => alternarSelecao(row.id)}
      aria-label={`Selecionar plantão de ${row.data} em ${row.vinculo_nome}`}
    />
  );
  const checkboxTodos = (
    <Checkbox
      isChecked={todosSelecionados}
      isIndeterminate={algunsSelecionados}
      onChange={alternarTodos}
      aria-label="Selecionar todos os plantões da lista"
    />
  );

  const contagemPorStatus = useMemo(() => {
    const contagem = {};
    for (const p of todos) {
      contagem[p.status] = (contagem[p.status] ?? 0) + 1;
    }
    return contagem;
  }, [todos]);

  // `stackProps` deixa o cartão mobile quebrar os badges em linhas sem mudar a coluna da tabela.
  const renderStatus = (row, stackProps) => (
    <HStack spacing={1.5} {...stackProps}>
      <StatusBadge status={row.status} />
      {row.schedule_status === "scheduled" ? (
        <Badge variant="statusSubtle" colorScheme={SCHEDULE_STATUS_META.scheduled.colorScheme}>
          {SCHEDULE_STATUS_META.scheduled.label}
        </Badge>
      ) : null}
    </HStack>
  );

  const renderAcoes = (row) => (
    <HStack justify="flex-end" spacing={1}>
      {row.status === "em_atraso" ? (
        <Button
          size="sm"
          variant="ghost"
          color="danger-text"
          rightIcon={<ArrowRight size={14} />}
          onClick={() => navigate(urlCobranca(row))}
        >
          Cobrar
        </Button>
      ) : null}
      <IconButton
        aria-label="Editar plantão"
        icon={<Pencil size={14} />}
        size="sm"
        variant="ghost"
        onClick={() => {
          setPlantaoEditando(row);
          edicaoDisclosure.onOpen();
        }}
      />
      <IconButton
        aria-label="Duplicar plantão"
        icon={<Copy size={14} />}
        size="sm"
        variant="ghost"
        onClick={() => {
          setPlantaoDuplicando(row);
          duplicarDisclosure.onOpen();
        }}
      />
    </HStack>
  );

  // Versão em cartão da mesma linha, usada abaixo de `md` no lugar da tabela.
  const renderCartao = (row) => (
    <VStack align="stretch" spacing={2} py={3}>
      <HStack justify="space-between" align="flex-start" spacing={3}>
        <HStack align="flex-start" spacing={3} minW={0}>
          <Box pt={0.5}>{renderSelecao(row)}</Box>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="medium">
              {row.vinculo_nome}
            </Text>
            <Text fontSize="xs" color="app-muted-fg">
              {row.data} · Previsão {row.previsao}
            </Text>
          </Box>
        </HStack>
        <Text fontSize="sm" fontWeight="semibold" flexShrink={0}>
          <CurrencyText value={row.valor} />
        </Text>
      </HStack>
      <HStack justify="space-between" spacing={2}>
        {renderStatus(row, { flexWrap: "wrap" })}
        {renderAcoes(row)}
      </HStack>
    </VStack>
  );

  const colunas = [
    { key: "selecao", label: checkboxTodos, render: (row) => renderSelecao(row) },
    { key: "data", label: "Data" },
    {
      key: "vinculo",
      label: "Vínculo",
      render: (row) => (
        <Box>
          <Text fontSize="sm" fontWeight="medium">
            {row.vinculo_nome}
          </Text>
          <Text fontSize="xs" color="app-muted-fg">
            Previsão {row.previsao}
          </Text>
        </Box>
      ),
    },
    { key: "valor", label: "Valor", align: "right", render: (row) => <CurrencyText value={row.valor} /> },
    { key: "status", label: "Status", render: (row) => renderStatus(row) },
    { key: "acao", label: "", render: (row) => renderAcoes(row) },
  ];

  return (
    <>
      <PageHeader
        title="Recebíveis"
        description="Todos os seus plantões e o status de recebimento. Selecione vários para mudar o status de uma vez."
        actions={
          <Button leftIcon={<Plus size={16} />} onClick={registrarPlantaoDisclosure?.onOpen}>
            Registrar plantão
          </Button>
        }
      />

      <SectionCard p={{ base: 3, md: 5 }}>
        <Wrap spacing={2} mb={4}>
          <WrapItem>
            <Button size="sm" variant={statusFiltro === "" ? "solid" : "outline"} onClick={() => setStatusFiltro("")}>
              Todos ({todos.length})
            </Button>
          </WrapItem>
          {STATUS_ORDEM.map((status) => (
            <WrapItem key={status}>
              <Button
                size="sm"
                variant={statusFiltro === status ? "solid" : "outline"}
                onClick={() => setStatusFiltro(statusFiltro === status ? "" : status)}
              >
                {STATUS_META[status].label} ({contagemPorStatus[status] ?? 0})
              </Button>
            </WrapItem>
          ))}
          <WrapItem flex={1} minW={{ base: "100%", sm: "180px" }} justifyContent="flex-end">
            <HStack spacing={2} w={{ base: "100%", sm: "auto" }}>
              <PeriodFilterPopover period={periodoFiltro} onChange={setPeriodoFiltro} />
              <Select size="sm" maxW={{ base: "none", sm: "240px" }} value={vinculoFiltro} onChange={(e) => setVinculoFiltro(e.target.value)} borderRadius="lg">
                <option value="">Todos os vínculos</option>
                {vinculos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nome}
                  </option>
                ))}
              </Select>
            </HStack>
          </WrapItem>
        </Wrap>
        {filtrados.length > 0 ? (
          <HStack display={{ base: "flex", md: "none" }} mb={2}>
            <Checkbox isChecked={todosSelecionados} isIndeterminate={algunsSelecionados} onChange={alternarTodos}>
              <Text fontSize="sm">Selecionar todos ({filtrados.length})</Text>
            </Checkbox>
          </HStack>
        ) : null}
        {plantoesSelecionados.length > 0 ? (
          <Box mb={3}>
            <BulkStatusBar
              shifts={plantoesSelecionados}
              onDone={limparSelecao}
              extraActions={
                <Button size="sm" variant="ghost" onClick={limparSelecao}>
                  Limpar seleção
                </Button>
              }
            />
          </Box>
        ) : null}
        {periodoFiltro?.ano ? (
          <Text fontSize="sm" color="app-muted-fg" mb={3}>
            {describePaymentMonth(periodoFiltro)}
          </Text>
        ) : null}

        <DataTable
          columns={colunas}
          rows={filtrados}
          isLoading={isLoading}
          renderMobileRow={renderCartao}
          emptyState={
            <EmptyState
              icon={Wallet}
              title="Nenhum plantão encontrado"
              description="Ajuste os filtros ou registre um novo plantão."
            />
          }
          footer={
            filtrados.length > 0 ? (
              <Box borderTop="1px solid" borderColor="app-border" mt={2} pt={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="app-muted-fg">
                    {filtrados.length} plantão(ões)
                  </Text>
                  <Text fontWeight="bold">
                    <CurrencyText value={total} />
                  </Text>
                </HStack>
              </Box>
            ) : null
          }
        />
      </SectionCard>

      <EditShiftModal isOpen={edicaoDisclosure.isOpen} onClose={edicaoDisclosure.onClose} shift={plantaoEditando} />
      <RegistrarPlantaoModal isOpen={duplicarDisclosure.isOpen} onClose={duplicarDisclosure.onClose} plantaoBase={plantaoDuplicando} />
    </>
  );
}
